import React, { useEffect, useState } from "react";
import axiosInstance from "../../../axiosInstance";
import { secureStorage } from "../../../utils/secureStorage";
import { FcApproval } from "react-icons/fc";

const LocationSelect = ({
  useFor = "country",
  onSelect,
  value = null,
  countryId = null,
  divisionId = null,
  cityId = null,
  placeholder,
}) => {
  const [Data, setData] = useState([]);
  const [FilteredData, setFilteredData] = useState([]);
  const [Show, setShow] = useState(false);
  const [Search, setSearch] = useState("");
  const [SelectedItem, setSelectedItem] = useState(null);
  const [Loading, setLoading] = useState(false);

  const type = useFor.toLowerCase();

  const Config = {
    country: {
      endpoint: "/admin/local/country/active",
      nameKey: "countryName",
      label: "country",
    },
    division: {
      endpoint: "/admin/local/division/active",
      nameKey: "divisionName",
      label: "division",
    },
    city: {
      endpoint: "/admin/local/city/active",
      nameKey: "cityName",
      label: "city",
    },
    neighbourhood: {
      endpoint: "/admin/local/neighbourhood/active",
      nameKey: "neighbourhoodName",
      label: "neighbourhood",
    },
    neighborhood: {
      endpoint: "/admin/local/neighbourhood/active",
      nameKey: "neighbourhoodName",
      label: "neighbourhood",
    },
  };

  const CurrentConfig = Config[type] || Config.country;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const adminToken = secureStorage.getItem("adminToken");

        const res = await axiosInstance.get(CurrentConfig.endpoint, {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        });

        setData(res.data.data || []);
      } catch (error) {
        console.error(`Fetch ${type} error:`, error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, CurrentConfig.endpoint]);

  useEffect(() => {
    let result = [...Data];

    // Filter divisions by country
    if (type === "division" && countryId) {
      result = result.filter(
        (item) => Number(item.countryId) === Number(countryId),
      );
    }

    // Filter cities by country and division
    if (type === "city") {
      if (countryId) {
        result = result.filter(
          (item) => Number(item.countryId) === Number(countryId),
        );
      }

      if (divisionId) {
        result = result.filter(
          (item) => Number(item.divisionId) === Number(divisionId),
        );
      }
    }

    // Filter neighbourhoods by country, division and city
    if (type === "neighbourhood" || type === "neighborhood") {
      if (countryId) {
        result = result.filter(
          (item) => Number(item.countryId) === Number(countryId),
        );
      }

      if (divisionId) {
        result = result.filter(
          (item) => Number(item.divisionId) === Number(divisionId),
        );
      }

      if (cityId) {
        result = result.filter(
          (item) => Number(item.cityId) === Number(cityId),
        );
      }
    }

    if (Search.trim()) {
      result = result.filter((item) =>
        String(item[CurrentConfig.nameKey] || "")
          .toLowerCase()
          .includes(Search.trim().toLowerCase()),
      );
    }

    setFilteredData(result);
  }, [
    Data,
    Search,
    type,
    CurrentConfig.nameKey,
    countryId,
    divisionId,
    cityId,
  ]);

  // Keep the selected item synchronized with the parent value
  useEffect(() => {
    if (value === null || value === undefined || value === "") {
      setSelectedItem(null);
      return;
    }

    const item = Data.find((item) => Number(item.id) === Number(value));

    setSelectedItem(item || null);
  }, [value, Data]);

  const handleSelect = (item) => {
    setSelectedItem(item);
    setShow(false);
    setSearch("");

    if (onSelect) {
      onSelect({
        ...item,
        name: item[CurrentConfig.nameKey],
        selectedType: type,
      });
    }
  };

  const handleClear = () => {
    setSelectedItem(null);
    setSearch("");

    if (onSelect) {
      onSelect(null);
    }
  };

  return (
    <div className="w-full relative">
      <div
        onClick={() => setShow((prev) => !prev)}
        className="w-full px-4 py-2 rounded-lg border focus:outline-none text-sm cursor-pointer select-none flex justify-between items-center gap-3"
      >
        <span className="truncate">
          {SelectedItem
            ? SelectedItem[CurrentConfig.nameKey]
            : placeholder || `Select ${CurrentConfig.label}`}
        </span>

        <span className="text-gray-500">⌄</span>
      </div>

      {Show && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShow(false)} />

          <div className="bg-white w-full max-h-[300px] overflow-y-auto p-3 absolute top-full mt-1 left-0 drop-shadow-lg rounded-lg z-20 border scrollbar-hide">
            <div className="space-y-3">
              <input
                type="text"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none text-sm"
                placeholder={`Search ${CurrentConfig.label}`}
                onChange={(e) => setSearch(e.target.value)}
                value={Search}
                onClick={(e) => e.stopPropagation()}
              />

              {SelectedItem && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-sm text-red-600 px-2"
                >
                  Clear selection
                </button>
              )}

              {Loading ? (
                <p className="text-center text-sm py-3">Loading...</p>
              ) : FilteredData.length > 0 ? (
                FilteredData.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className="flex gap-2 justify-between items-center bg-white hover:bg-[#d7eaff] p-2 rounded-lg cursor-pointer"
                  >
                    <p className="text-sm font-medium">
                      {item[CurrentConfig.nameKey]}
                    </p>

                    {Number(SelectedItem?.id) === Number(item.id) && (
                      <FcApproval className="text-md shrink-0" />
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-sm text-gray-500 py-3">
                  No {CurrentConfig.label} found
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LocationSelect;
